import React from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import * as ETVConstants from '../ETVConstants.js';
import * as GeoAdConstants from './GeoAdConstants.js';
import ETVPage from '../tools/ETVPage.js';
import ETVMessagePopup from '../tools/ETVMessagePopup.js';
import ETVActionButton from '../tools/ETVActionButton.js';
import { buildingApi } from '../building/BuildingApi.js';

const PRICED_TRAFFIC_LEVELS = ['MORNING_PEAK', 'LUNCH_PEAK', 'DAYTIME_FLOW', 'EVENING_PEAK', 'OFF_PEAK'];
const RECOMMENDED_CPM = 100;
const TRAFFIC_LEVELS = [...PRICED_TRAFFIC_LEVELS, 'NO_TRAFFIC'];
const SLOT_MINUTES = 30;
const SLOTS_PER_DAY = 24 * (60 / SLOT_MINUTES);
const WEEKDAYS = [
	{ value:1, label:'Mon' },
	{ value:2, label:'Tue' },
	{ value:3, label:'Wed' },
	{ value:4, label:'Thu' },
	{ value:5, label:'Fri' },
	{ value:6, label:'Sat' },
	{ value:0, label:'Sun' },
];
const TIME_SLOTS = Array.from({length:SLOTS_PER_DAY}, (_, slot) => slot);

const TRAFFIC_STYLE = {
	MORNING_PEAK: { label:'Morning peak', color:'#c2410c', text:'#ffffff' },
	LUNCH_PEAK: { label:'Lunch peak', color:'#d97706', text:'#ffffff' },
	DAYTIME_FLOW: { label:'Daytime flow', color:'#f6c85f', text:'#3f2d00' },
	EVENING_PEAK: { label:'Evening peak', color:'#c2410c', text:'#ffffff' },
	OFF_PEAK: { label:'Off-peak', color:'#fff3bf', text:'#5c4400', buttonText:'#5c4400' },
	NO_TRAFFIC: { label:'No traffic', color:'#e5e7eb', text:'#374151', buttonText:'#374151' },
};

function formatSlot(slot) {
	var clampedSlot = Math.max(0, Math.min(SLOTS_PER_DAY, slot));
	var totalMinutes = clampedSlot * SLOT_MINUTES;
	var hour = Math.floor(totalMinutes / 60);
	var minute = totalMinutes % 60;
	return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function parseTimeToSlot(value, fallback, roundUp=false) {
	if(value===undefined || value===null) return fallback;
	var parts = String(value).split(':');
	var hour = parseInt(parts[0], 10);
	var minute = parseInt(parts[1] || '0', 10);
	if(Number.isNaN(hour) || Number.isNaN(minute)) return fallback;
	var totalMinutes = Math.max(0, Math.min(24 * 60, (hour * 60) + minute));
	var slot = roundUp?Math.ceil(totalMinutes / SLOT_MINUTES):Math.floor(totalMinutes / SLOT_MINUTES);
	return Math.max(0, Math.min(SLOTS_PER_DAY, slot));
}

function createEmptyWeeklySlots() {
	var slots = {};
	WEEKDAYS.forEach((day) => {
		slots[day.value] = Array(SLOTS_PER_DAY).fill('NO_TRAFFIC');
	});
	return slots;
}

function fillSlotRange(slots, weekdays, startTime, endTime, level) {
	var startSlot = parseTimeToSlot(startTime, 0);
	var endSlot = parseTimeToSlot(endTime, startSlot + 1, true);
	weekdays.forEach((weekday) => {
		if(!slots[weekday]) return;
		for(var slot=startSlot; slot<endSlot && slot<SLOTS_PER_DAY; slot++) {
			slots[weekday][slot] = level;
		}
	});
}

function normalizeAirtimePercentage(value) {
	var parsed = Number(value);
	if(!Number.isFinite(parsed)) return 50;
	return Math.max(20, Math.min(100, Math.round(parsed / 10) * 10));
}

class GeoAdConfig extends React.Component {

	state = {
		isGeoadLoading:false,
		isSaving:false,
		geoad_locations:[],
		selected_location_id:'',
		geoad_enabled:false,
		approval_required:true,
		geoad_rates:{...GeoAdConstants.GEOAD_DEFAULT_RATES},
		weekly_slots:createEmptyWeeklySlots(),
		selected_traffic_level:'MORNING_PEAK',
		is_painting:false,
		population:'',
		advertising_format:'16:9',
		available_screens:[],
		advertising_screen_ids:[],
		location_image:'',
		location_image_preview:'',
		location_image_blob:null,
		airtime_percentage:50,
		window:'',
		info_title:'',
		info_message:'',
		info_type:'info',
		boostly_available:null,
		boostly_operators:[],
		selected_operator_scope_id:'',
		isOperatorLoading:false,
	}

	componentDidMount() {
		document.addEventListener('mouseup', this.stopPainting);
		this.loadGeoadLocations();
	}

	componentWillUnmount() {
		document.removeEventListener('mouseup', this.stopPainting);
	}

	stopPainting = () => {
		if(this.state.is_painting) {
			this.setState({is_painting:false});
		}
	}

	getGeoAdHeaders() {
		return {
			'user_id': localStorage.getItem("etv.user_id"),
			'access_token': localStorage.getItem("etv.access_token"),
		};
	}

	loadGeoadLocations() {
		this.setState({isGeoadLoading:true});
		var urlStr = GeoAdConstants.GEOAD_BACKEND+"?action=get_locations";
		fetch(urlStr,{ headers: this.getGeoAdHeaders() })
			.then(res => res.json())
			.then((result) => {
				var locations = result.locations || [];
				var selectedLocationID = locations.length>0?locations[0].location_id:'';
				this.setState({
					geoad_locations:locations,
					selected_location_id:selectedLocationID,
					isGeoadLoading:false,
				});
				if(selectedLocationID) {
					this.loadGeoadConfig(selectedLocationID);
					this.loadBoostlyOperators(selectedLocationID);
				}
			})
			.catch((error) => {
				console.error(error);
				this.setState({isGeoadLoading:false});
			});
	}

	loadBoostlyOperators = async (locationID) => {
		if(!locationID) {
			this.setState({
				boostly_available:null,
				boostly_operators:[],
				selected_operator_scope_id:'',
				isOperatorLoading:false,
			});
			return;
		}
		this.setState({isOperatorLoading:true});
		try {
			const result = await buildingApi('admin_list_boostly_operators', {
				query:{location_id:locationID},
			});
			this.setState({
				boostly_available:Boolean(result.boostly_available),
				boostly_operators:result.operators || [],
				selected_operator_scope_id:result.selected_operator_scope_id || '',
				isOperatorLoading:false,
			});
		}catch(error) {
			console.error(error);
			this.setState({
				boostly_available:null,
				boostly_operators:[],
				selected_operator_scope_id:'',
				isOperatorLoading:false,
			});
			this.showInfoMessage('Boostly Operator', error.message || 'Could not load available operators', 'error');
		}
	}

	selectBoostlyOperator = (operatorScopeID) => {
		if(this.state.isSaving) return;
		this.setState({selected_operator_scope_id:operatorScopeID});
	}

	toggleBoostlyEnabled = (enabled) => {
		if(enabled) {
			const missingItems = this.getActivationMissingItems();
			if(missingItems.length===0) {
				this.setState({geoad_enabled:true});
				return;
			}
			this.showInfoMessage(
				'Boostly setup incomplete',
				`Complete these settings before enabling bookings: ${missingItems.join(', ')}.`,
				'warning',
			);
			return;
		}
		this.setState({geoad_enabled:false});
	}

	getActivationMissingItems = () => {
		const missing = [];
		const airtime = Number(this.state.airtime_percentage);
		const population = Number(this.state.population);
		const supportedFormats = ['4:3','3:4','16:9','9:16','4:5','1.91:1'];
		const selectedOperator = this.state.boostly_operators.find(
			(operator)=>String(operator.id)===String(this.state.selected_operator_scope_id) && operator.status==='ACTIVE',
		);
		const hasScreen = this.state.advertising_screen_ids.length>0;
		const hasSchedule = this.weeklySlotsToSchedule().length>0;
		const hasImage = Boolean(this.state.location_image_blob || this.state.location_image);

		if(!selectedOperator) missing.push('active Boostly operator');
		if(!Number.isFinite(airtime) || airtime<20 || airtime>100) missing.push('airtime percentage');
		if(!Number.isInteger(population) || population<=0) missing.push('building population');
		if(!supportedFormats.includes(this.state.advertising_format)) missing.push('advertising format');
		if(!hasImage) missing.push('building image');
		if(!hasScreen) missing.push('at least one advertising screen');
		if(this.validateRates(true)) missing.push('pricing for every traffic category');
		if(!hasSchedule) missing.push('weekly calendar');
		return missing;
	}

	loadGeoadConfig(locationID) {
		this.setState({isGeoadLoading:true});
		var urlStr = GeoAdConstants.GEOAD_BACKEND+"?action=get_config&location_id="+locationID;
		fetch(urlStr,{ headers: this.getGeoAdHeaders() })
			.then(res => res.json())
			.then((result) => {
				if(result.result) {
					this.applyGeoAdConfig(result);
				}
				this.setState({isGeoadLoading:false});
			})
			.catch((error) => {
				console.error(error);
				this.setState({isGeoadLoading:false});
			});
	}

	applyGeoAdConfig(result) {
		var geoadSettings = result.geoad_settings || {};
		var geoadRates = {...GeoAdConstants.GEOAD_DEFAULT_RATES};
		if(result.geoad_rates) {
			for(var i=0; i<result.geoad_rates.length; i++) {
				var rate = result.geoad_rates[i];
				if(rate.level && geoadRates[rate.level]!==undefined) {
					geoadRates[rate.level] = rate.price;
				}
			}
		}

		this.setState({
			geoad_enabled: geoadSettings.geoad_enabled===1 || geoadSettings.geoad_enabled===true,
			approval_required: geoadSettings.approval_required!==0 && geoadSettings.approval_required!==false,
			geoad_rates: geoadRates,
			weekly_slots: this.scheduleToWeeklySlots(result.geoad_peak_schedule || []),
			population: geoadSettings.population || '',
			advertising_format: geoadSettings.advertising_format || '16:9',
			available_screens: result.available_screens || [],
			advertising_screen_ids: geoadSettings.advertising_screens_configured
				? (result.advertising_screen_ids || [])
				: (result.available_screens || []).map((screen)=>screen.device_id),
			location_image: geoadSettings.location_image || '',
			location_image_preview: geoadSettings.location_image || '',
			location_image_blob: null,
			airtime_percentage: normalizeAirtimePercentage(geoadSettings.ad_ratio_percent || this.state.airtime_percentage),
		});
	}

	scheduleToWeeklySlots(schedule) {
		var slots = createEmptyWeeklySlots();
		schedule.forEach((item) => {
			var weekday = Number(item.weekday);
			if(!slots[weekday]) return;

			var level = TRAFFIC_LEVELS.indexOf(item.level)>=0?item.level:'DAYTIME_FLOW';
			var startSlot = parseTimeToSlot(item.start_time, 0);
			var endSlot = parseTimeToSlot(item.end_time, startSlot + 1, true);
			for(var slot=startSlot; slot<endSlot && slot<SLOTS_PER_DAY; slot++) {
				slots[weekday][slot] = level;
			}
		});
		return slots;
	}

	weeklySlotsToSchedule() {
		var schedule = [];
		WEEKDAYS.forEach((day) => {
			var slots = this.state.weekly_slots[day.value];
			var startSlot = 0;
			var currentLevel = slots[0];
			for(var slot=1; slot<=SLOTS_PER_DAY; slot++) {
				var nextLevel = slot<SLOTS_PER_DAY?slots[slot]:null;
				if(nextLevel!==currentLevel) {
					if(currentLevel!=='NO_TRAFFIC') {
						schedule.push({
							weekday:day.value,
							level:currentLevel,
							label:TRAFFIC_STYLE[currentLevel].label,
							start_time:formatSlot(startSlot),
							end_time:formatSlot(slot),
							active:true,
						});
					}
					startSlot = slot;
					currentLevel = nextLevel;
				}
			}
		});
		return schedule;
	}

	handleGeoadLocationChange(locationID) {
		this.setState({
			selected_location_id:locationID,
			boostly_available:null,
			boostly_operators:[],
			selected_operator_scope_id:'',
		});
		if(locationID) {
			this.loadGeoadConfig(locationID);
			this.loadBoostlyOperators(locationID);
		}
	}

	getSelectedLocation() {
		return this.state.geoad_locations.find((location) => String(location.location_id)===String(this.state.selected_location_id)) || null;
	}

	getLocationTypeLabel(locationType) {
		var key = {
			100:'location_type_hotel_5_star',
			101:'location_type_hotel_4_star',
			102:'location_type_hotel_3_star',
			103:'location_type_hotel_others',
			200:'location_type_mall_luxury',
			201:'location_type_mall_standard',
			300:'location_type_office_high_rise',
			301:'location_type_office_mid_rise',
			302:'location_type_office_low_rise',
			400:'location_type_residential_condo',
			401:'location_type_residential_hdb',
			501:'location_type_restaurant',
			502:'location_type_cafe',
		}[Number(locationType)];
		return key?ETVConstants.trans(key):'generic building';
	}

	createDefaultWeeklySlots(locationType) {
		var slots = createEmptyWeeklySlots();
		var weekdays = [1,2,3,4,5];
		var weekend = [6,0];
		var allDays = [1,2,3,4,5,6,0];
		var type = Number(locationType);

		if(type>=300 && type<=302) {
			fillSlotRange(slots, weekdays, '07:30', '09:30', 'MORNING_PEAK');
			fillSlotRange(slots, weekdays, '09:30', '12:00', 'DAYTIME_FLOW');
			fillSlotRange(slots, weekdays, '12:00', '14:00', 'LUNCH_PEAK');
			fillSlotRange(slots, weekdays, '14:00', '17:30', 'DAYTIME_FLOW');
			fillSlotRange(slots, weekdays, '17:30', '19:30', 'EVENING_PEAK');
			fillSlotRange(slots, [6], '09:00', '13:00', 'OFF_PEAK');
		}else if(type>=400 && type<=401) {
			fillSlotRange(slots, weekdays, '07:00', '09:00', 'MORNING_PEAK');
			fillSlotRange(slots, weekdays, '09:00', '17:00', 'OFF_PEAK');
			fillSlotRange(slots, weekdays, '17:00', '22:00', 'EVENING_PEAK');
			fillSlotRange(slots, weekend, '08:00', '11:00', 'MORNING_PEAK');
			fillSlotRange(slots, weekend, '11:00', '14:00', 'LUNCH_PEAK');
			fillSlotRange(slots, weekend, '14:00', '18:00', 'DAYTIME_FLOW');
			fillSlotRange(slots, weekend, '18:00', '22:00', 'EVENING_PEAK');
		}else if(type>=100 && type<=103) {
			fillSlotRange(slots, allDays, '06:30', '10:00', 'MORNING_PEAK');
			fillSlotRange(slots, allDays, '10:00', '12:00', 'DAYTIME_FLOW');
			fillSlotRange(slots, allDays, '12:00', '14:00', 'LUNCH_PEAK');
			fillSlotRange(slots, allDays, '14:00', '18:00', 'DAYTIME_FLOW');
			fillSlotRange(slots, allDays, '18:00', '23:00', 'EVENING_PEAK');
			fillSlotRange(slots, allDays, '23:00', '24:00', 'OFF_PEAK');
		}else if(type>=200 && type<=201) {
			fillSlotRange(slots, weekdays, '10:00', '12:00', 'DAYTIME_FLOW');
			fillSlotRange(slots, weekdays, '12:00', '14:00', 'LUNCH_PEAK');
			fillSlotRange(slots, weekdays, '14:00', '18:00', 'DAYTIME_FLOW');
			fillSlotRange(slots, weekdays, '18:00', '22:00', 'EVENING_PEAK');
			fillSlotRange(slots, weekend, '10:00', '12:00', 'MORNING_PEAK');
			fillSlotRange(slots, weekend, '12:00', '14:00', 'LUNCH_PEAK');
			fillSlotRange(slots, weekend, '14:00', '18:00', 'DAYTIME_FLOW');
			fillSlotRange(slots, weekend, '18:00', '22:00', 'EVENING_PEAK');
		}else if(type===501) {
			fillSlotRange(slots, allDays, '11:00', '14:30', 'LUNCH_PEAK');
			fillSlotRange(slots, allDays, '14:30', '18:00', 'OFF_PEAK');
			fillSlotRange(slots, allDays, '18:00', '22:30', 'EVENING_PEAK');
		}else if(type===502) {
			fillSlotRange(slots, allDays, '07:00', '10:00', 'MORNING_PEAK');
			fillSlotRange(slots, allDays, '10:00', '12:00', 'DAYTIME_FLOW');
			fillSlotRange(slots, allDays, '12:00', '14:00', 'LUNCH_PEAK');
			fillSlotRange(slots, allDays, '14:00', '17:00', 'DAYTIME_FLOW');
			fillSlotRange(slots, allDays, '17:00', '19:00', 'OFF_PEAK');
		}else{
			fillSlotRange(slots, weekdays, '07:30', '09:30', 'MORNING_PEAK');
			fillSlotRange(slots, weekdays, '12:00', '14:00', 'LUNCH_PEAK');
			fillSlotRange(slots, weekdays, '09:30', '12:00', 'DAYTIME_FLOW');
			fillSlotRange(slots, weekdays, '14:00', '17:30', 'DAYTIME_FLOW');
			fillSlotRange(slots, weekdays, '17:30', '20:00', 'EVENING_PEAK');
		}

		return slots;
	}

	applyDefaultTraffic() {
		if(this.state.isSaving) return;
		var location = this.getSelectedLocation();
		var locationType = location?location.location_type:null;
		this.setState({weekly_slots:this.createDefaultWeeklySlots(locationType), is_painting:false});
		this.showInfoMessage('Default Traffic', `Applied ${this.getLocationTypeLabel(locationType)} traffic pattern. You can still adjust the calendar before saving.`, 'success');
	}

	updateGeoadRate(level, price) {
		this.setState({geoad_rates:{...this.state.geoad_rates,[level]:price}});
	}

	getRecommendedPrices() {
		var population = Number(this.state.population || 0);
		var airtimeRatio = Number(this.state.airtime_percentage || 0) / 100;
		var peak = population>0?(population / 1000) * RECOMMENDED_CPM * airtimeRatio:0;
		return {
			MORNING_PEAK: Number(peak.toFixed(2)),
			LUNCH_PEAK: Number((peak * 0.75).toFixed(2)),
			DAYTIME_FLOW: Number((peak * 0.5).toFixed(2)),
			EVENING_PEAK: Number(peak.toFixed(2)),
			OFF_PEAK: Number((peak * 0.25).toFixed(2)),
		};
	}

	formatRecommendedPrice(value) {
		return `${GeoAdConstants.GEOAD_CURRENCY} ${Number(value || 0).toFixed(2)}`;
	}

	applyRecommendedPrices() {
		if(this.state.isSaving) return;
		var recommendedPrices = this.getRecommendedPrices();
		this.setState({
			geoad_rates:{
				...this.state.geoad_rates,
				MORNING_PEAK: recommendedPrices.MORNING_PEAK.toFixed(2),
				LUNCH_PEAK: recommendedPrices.LUNCH_PEAK.toFixed(2),
				DAYTIME_FLOW: recommendedPrices.DAYTIME_FLOW.toFixed(2),
				EVENING_PEAK: recommendedPrices.EVENING_PEAK.toFixed(2),
				OFF_PEAK: recommendedPrices.OFF_PEAK.toFixed(2),
			},
		});
	}

	validateRates(requirePositive=false) {
		for(var i=0; i<PRICED_TRAFFIC_LEVELS.length; i++) {
			var level = PRICED_TRAFFIC_LEVELS[i];
			var price = Number(this.state.geoad_rates[level]);
			if(!Number.isFinite(price) || price < 0 || (requirePositive && price<=0)) {
				return 'Please enter valid prices for every traffic slot.';
			}
		}
		return null;
	}

	paintSlot(weekday, slot, level) {
		var slots = {...this.state.weekly_slots};
		slots[weekday] = [...slots[weekday]];
		slots[weekday][slot] = level;
		this.setState({weekly_slots:slots});
	}

	startPainting(weekday, slot, event) {
		if(this.state.isSaving) return;
		event.preventDefault();
		this.paintSlot(weekday, slot, this.state.selected_traffic_level);
		this.setState({is_painting:true});
	}

	paintIfDragging(weekday, slot) {
		if(this.state.is_painting) {
			this.paintSlot(weekday, slot, this.state.selected_traffic_level);
		}
	}

	clearWeek() {
		if(this.state.isSaving) return;
		this.setState({weekly_slots:createEmptyWeeklySlots()});
	}

	resizeLocationImage(file) {
		return new Promise((resolve, reject) => {
			if(!file || !file.type || !file.type.startsWith('image/')) {
				reject(new Error('Please select an image file.'));
				return;
			}
			var image = new Image();
			var objectUrl = URL.createObjectURL(file);
			image.onload = () => {
				var canvas = document.createElement('canvas');
				canvas.width = 512;
				canvas.height = 512;
				var context = canvas.getContext('2d');
				var sourceSize = Math.min(image.width, image.height);
				var sourceX = Math.max(0, Math.floor((image.width - sourceSize) / 2));
				var sourceY = Math.max(0, Math.floor((image.height - sourceSize) / 2));
				context.fillStyle = '#ffffff';
				context.fillRect(0, 0, 512, 512);
				context.drawImage(image, sourceX, sourceY, sourceSize, sourceSize, 0, 0, 512, 512);
				canvas.toBlob((blob) => {
					URL.revokeObjectURL(objectUrl);
					if(!blob) {
						reject(new Error('Could not process the image.'));
						return;
					}
					resolve({
						blob,
						preview: canvas.toDataURL('image/jpeg', 0.9),
					});
				}, 'image/jpeg', 0.9);
			};
			image.onerror = () => {
				URL.revokeObjectURL(objectUrl);
				reject(new Error('Could not read the image.'));
			};
			image.src = objectUrl;
		});
	}

	handleLocationImageChange(event) {
		if(this.state.isSaving) return;
		var file = event.target.files && event.target.files[0];
		if(!file) return;
		this.resizeLocationImage(file)
			.then((image) => {
				this.setState({
					location_image_blob:image.blob,
					location_image_preview:image.preview,
				});
			})
			.catch((error) => {
				this.showInfoMessage('Building Image', error.message || 'Could not process the image.', 'error');
			});
		event.target.value = '';
	}

	async uploadLocationImageIfNeeded() {
		if(!this.state.location_image_blob) return this.state.location_image;

		var signResponse = await fetch(GeoAdConstants.GEOAD_BACKEND+"?action=create_location_image_upload_url",{
			method:'POST',
			headers:{...this.getGeoAdHeaders(),'Content-Type':'application/json'},
			body:JSON.stringify({
				location_id:this.state.selected_location_id,
				filename:'building.jpg',
				mime_type:'image/jpeg',
				file_size_bytes:this.state.location_image_blob.size,
			}),
		});
		var signResult = await signResponse.json();
		if(!signResult.result) {
			throw new Error(signResult.error || 'Could not prepare image upload.');
		}

		var uploadResponse = await fetch(signResult.upload_url,{
			method:'PUT',
			headers:{'Content-Type':'image/jpeg'},
			body:this.state.location_image_blob,
		});
		if(!uploadResponse.ok) {
			throw new Error('Could not upload building image.');
		}
		return signResult.file_url;
	}

	async saveConfig() {
		if(!this.state.selected_location_id || this.state.isSaving) return;
		if(this.state.geoad_enabled) {
			const missingItems = this.getActivationMissingItems();
			if(missingItems.length>0) {
				this.showInfoMessage(
					'Boostly setup incomplete',
					`Complete these settings before enabling bookings: ${missingItems.join(', ')}.`,
					'warning',
				);
				return;
			}
		}

		var rateError = this.validateRates();
		if(rateError) {
			this.showInfoMessage('GeoAd Pricing', rateError, 'warning');
			return;
		}

		this.setState({isSaving:true, is_painting:false});
		var locationImage = '';
		try {
			locationImage = await this.uploadLocationImageIfNeeded();
		}catch(error) {
			console.error(error);
			this.setState({isSaving:false});
			this.showInfoMessage('Building Image', error.message || 'Could not upload building image', 'error');
			return;
		}
		var payload = {
			location_id:this.state.selected_location_id,
			operator_scope_id:this.state.selected_operator_scope_id===''?null:Number(this.state.selected_operator_scope_id),
			geoad_enabled:this.state.geoad_enabled,
			currency:GeoAdConstants.GEOAD_CURRENCY,
			ad_ratio_percent:this.state.airtime_percentage,
			approval_required:this.state.approval_required,
			max_ad_duration_sec:30,
			default_image_duration_sec:10,
			population:this.state.population===''?null:Number(this.state.population),
			advertising_format:this.state.advertising_format,
			advertising_screen_ids:this.state.advertising_screen_ids,
			location_image:locationImage,
			rates:this.state.geoad_rates,
			peak_schedule:this.weeklySlotsToSchedule(),
		};
		fetch(GeoAdConstants.GEOAD_BACKEND+"?action=set_config",{
			method:'POST',
			headers:{...this.getGeoAdHeaders(),'Content-Type':'application/json'},
			body:JSON.stringify(payload),
		})
			.then(res => res.json())
			.then((result) => {
				if(result.result) {
					this.applyGeoAdConfig(result);
					this.showInfoMessage('Boostly Settings', 'Settings saved', 'success');
				}else{
					const errorMessage = result.error==='boostly_setup_weekly_calendar_invalid'
						? 'The weekly calendar contains an invalid time range. Each period must end after it starts.'
						: (result.error || 'Could not save GeoAd settings');
					this.showInfoMessage('GeoAd Settings', errorMessage, 'error');
				}
				this.setState({isSaving:false});
			})
			.catch((error) => {
				console.error(error);
				this.setState({isSaving:false});
				this.showInfoMessage('GeoAd Settings', 'Could not save GeoAd settings', 'error');
			});
	}

	showInfoMessage(title,text,type='info') {
		this.setState({info_title:title,info_message:text,info_type:type,window:'message_div'});
	}

	toggleAdvertisingScreen(deviceID) {
		this.setState((state) => ({
			advertising_screen_ids:state.advertising_screen_ids.includes(deviceID)
				? state.advertising_screen_ids.filter((id) => id!==deviceID)
				: [...state.advertising_screen_ids, deviceID],
		}));
	}

	closeInfoMessage() {
		this.setState({info_message:'',window:''});
	}

	renderTrafficBrushes() {
		return (
			<Grid container spacing={1} sx={{marginTop:1}}>
				{TRAFFIC_LEVELS.map((level) => {
					var style = TRAFFIC_STYLE[level];
					var selected = this.state.selected_traffic_level===level;
					return (
						<Grid item xs={6} sm={2} key={level}>
							<Button
								fullWidth
								variant={selected?'contained':'outlined'}
								disabled={this.state.isSaving}
								onClick={()=>this.setState({selected_traffic_level:level})}
								sx={{
									backgroundColor:selected?style.color:'transparent',
									borderColor:style.color,
									color:selected?style.text:(style.buttonText || style.color),
									'&:hover':{backgroundColor:selected?style.color:'rgba(0,0,0,0.04)', borderColor:style.color},
								}}>
								{style.label}
							</Button>
						</Grid>
					);
				})}
			</Grid>
		);
	}

	renderWeeklyCalendar() {
		return (
			<Paper variant="outlined" sx={{marginTop:2, overflowX:'auto'}}>
				<div
					style={{
						display:'grid',
						gridTemplateColumns:'64px repeat(7, minmax(82px, 1fr))',
						minWidth:720,
						userSelect:'none',
					}}>
					<div style={{height:36, borderRight:'1px solid #ddd', borderBottom:'1px solid #ddd'}} />
					{WEEKDAYS.map((day) => (
						<div key={day.value} style={{height:36, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:600, borderRight:'1px solid #ddd', borderBottom:'1px solid #ddd'}}>
							{day.label}
						</div>
					))}

					{TIME_SLOTS.map((slot) => (
						<React.Fragment key={slot}>
							<div style={{height:30, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, color:'#5f6368', borderRight:'1px solid #ddd', borderBottom:'1px solid #eee'}}>
								{formatSlot(slot)}
							</div>
							{WEEKDAYS.map((day) => {
								var level = this.state.weekly_slots[day.value][slot];
								var style = TRAFFIC_STYLE[level];
								return (
									<div
										key={`${day.value}-${slot}`}
										title={`${day.label} ${formatSlot(slot)} ${style.label}`}
										onMouseDown={(event)=>this.startPainting(day.value, slot, event)}
										onMouseEnter={()=>this.paintIfDragging(day.value, slot)}
										style={{
											height:30,
											backgroundColor:style.color,
											borderRight:'1px solid #ddd',
											borderBottom:'1px solid #eee',
											cursor:this.state.isSaving?'not-allowed':'crosshair',
											boxSizing:'border-box',
										}}
									/>
								);
							})}
						</React.Fragment>
					))}
				</div>
			</Paper>
		);
	}

	renderSettings() {
		if(!this.state.selected_location_id || this.state.boostly_available!==true) return null;
		var recommendedPrices = this.getRecommendedPrices();
		var activationMissingItems = this.getActivationMissingItems();

		return (
			<span>
				<Paper variant="outlined" sx={{p:2,mt:2,mb:2}}>
					<Typography variant="h6">Settings</Typography>
					<Grid container spacing={2} alignItems="center" sx={{marginTop:0}}>
						<Grid item xs={12} sm={4}>
							<FormControl fullWidth variant="outlined" margin="normal">
								<InputLabel>Airtime percentage</InputLabel>
								<Select
									value={this.state.airtime_percentage}
									disabled={this.state.isSaving}
									onChange={(e)=>this.setState({airtime_percentage:e.target.value})}
									label="Airtime percentage" >
									{[20,30,40,50,60,70,80,90,100].map((percentage) => (
										<MenuItem value={percentage} key={percentage}>{percentage}%</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12} sm={4}>
							<TextField
								fullWidth
								type="number"
								label="Building population"
								value={this.state.population}
								disabled={this.state.isSaving}
								InputProps={{endAdornment:<InputAdornment position="end">people</InputAdornment>}}
								onChange={(e)=>this.setState({population:e.target.value})}
							/>
						</Grid>
						<Grid item xs={12} sm={4}>
							<FormControl fullWidth>
								<InputLabel>Advertising format</InputLabel>
								<Select value={this.state.advertising_format} disabled={this.state.isSaving}
									onChange={(e)=>this.setState({advertising_format:e.target.value})} label="Advertising format">
									<MenuItem value="4:3">4:3 (landscape)</MenuItem>
									<MenuItem value="3:4">3:4 (portrait)</MenuItem>
									<MenuItem value="16:9">16:9 (landscape)</MenuItem>
									<MenuItem value="9:16">9:16 (portrait)</MenuItem>
									<MenuItem value="4:5">4:5 (Instagram portrait)</MenuItem>
									<MenuItem value="1.91:1">1.91:1 (Instagram landscape)</MenuItem>
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12}>
							<Paper variant="outlined" sx={{p:2,bgcolor:'background.default'}}>
							<Typography variant="h6">Advertising screens and Web Players</Typography>
							<Typography variant="body2" color="text.secondary" sx={{mb:1}}>
								Only selected devices at this building will request and display Boostly advertising.
							</Typography>
							<Box sx={{display:'flex', gap:1, mb:1}}>
								<Button size="small" variant="outlined" disabled={this.state.isSaving || this.state.available_screens.length===0}
									onClick={()=>this.setState({advertising_screen_ids:this.state.available_screens.map((screen)=>screen.device_id)})}>Select all</Button>
								<Button size="small" variant="outlined" disabled={this.state.isSaving || this.state.advertising_screen_ids.length===0}
									onClick={()=>this.setState({advertising_screen_ids:[]})}>Select none</Button>
							</Box>
							<Paper variant="outlined" sx={{p:1, maxHeight:240, overflowY:'auto'}}>
								{this.state.available_screens.map((screen) => (
									<FormControlLabel key={screen.device_id}
									control={<Checkbox checked={this.state.advertising_screen_ids.includes(screen.device_id)}
										disabled={this.state.isSaving} onChange={()=>this.toggleAdvertisingScreen(screen.device_id)} />}
									label={`${screen.device_name || screen.device_id}${Number(screen.device_type)===3?' (Web Player)':''}`} />
							))}
							{this.state.available_screens.length===0 && <Typography variant="body2" color="text.secondary">No screens or Web Players are assigned to this building.</Typography>}
							</Paper>
							</Paper>
						</Grid>
						<Grid item xs={12}>
							<FormControlLabel
								control={
									<Switch
										checked={this.state.approval_required}
										disabled={this.state.isSaving}
										onChange={(e)=>this.setState({approval_required:e.target.checked})}
									/>
								}
								label="Require approval before publishing"
							/>
						</Grid>
					</Grid>
				</Paper>

				<Paper variant="outlined" sx={{p:2,mb:2}}>
					<Typography variant="h6">Pricing</Typography>
					<Typography variant="body2" color="text.secondary" style={{marginTop:6}}>
						Recommended peak pricing is based on population, CPM {RECOMMENDED_CPM}, and airtime percentage. Lunch, daytime flow, and off-peak are scaled from the peak rate.
					</Typography>
					<Grid container spacing={2} sx={{marginTop:1}}>
						<Grid item xs={12} sm={4}>
							<TextField
								fullWidth
								label="Currency"
								value={GeoAdConstants.GEOAD_CURRENCY}
								disabled
							/>
						</Grid>
						{PRICED_TRAFFIC_LEVELS.map((level) => (
							<Grid item xs={12} sm={4} key={level}>
								<TextField
									fullWidth
									type="number"
									label={`${TRAFFIC_STYLE[level].label} rate`}
									value={this.state.geoad_rates[level]}
									disabled={this.state.isSaving}
									InputProps={{startAdornment:<InputAdornment position="start">{GeoAdConstants.GEOAD_CURRENCY}</InputAdornment>}}
									helperText={`Recommended ${this.formatRecommendedPrice(recommendedPrices[level])}`}
									onChange={(e)=>this.updateGeoadRate(level, e.target.value)}
								/>
							</Grid>
						))}
						<Grid item xs={12}>
							<Button
								variant="outlined"
								disabled={this.state.isSaving || Number(this.state.population || 0)<=0}
								onClick={()=>this.applyRecommendedPrices()}>
								Use recommended prices
							</Button>
						</Grid>
					</Grid>
				</Paper>

				<Paper variant="outlined" sx={{p:2,mb:2}}>
					<Grid container spacing={2} alignItems="center">
						<Grid item xs={12} sm={6}>
							<Typography variant="h6">Weekly Calendar</Typography>
						</Grid>
						<Grid item xs={12} sm={3}>
							<Button fullWidth variant="outlined" disabled={this.state.isSaving} onClick={()=>this.applyDefaultTraffic()}>Set default traffic</Button>
						</Grid>
						<Grid item xs={12} sm={3}>
							<Button fullWidth variant="outlined" color="error" disabled={this.state.isSaving} onClick={()=>this.clearWeek()}>Clear Week</Button>
						</Grid>
					</Grid>
					{this.renderTrafficBrushes()}
					{this.renderWeeklyCalendar()}
				</Paper>

				<Box sx={{
					p:2,
					border:'2px solid',
					borderColor:this.state.geoad_enabled?'warning.main':activationMissingItems.length===0?'success.main':'divider',
					borderRadius:2,
					backgroundColor:this.state.geoad_enabled?'rgba(255, 111, 0, 0.08)':'background.paper',
					display:'flex',
					alignItems:{xs:'flex-start',sm:'center'},
					flexDirection:{xs:'column',sm:'row'},
					justifyContent:'space-between',
					gap:2,
				}}>
					<Box>
						<Typography variant="subtitle1" sx={{fontWeight:700}}>Enable Boostly bookings</Typography>
						<Typography variant="body2" color="text.secondary">
							Allow shops to discover and book advertising slots in this building.
						</Typography>
						{activationMissingItems.length>0 && (
							<Box component="ul" sx={{my:1,pl:2.5,color:'text.secondary'}}>
								{activationMissingItems.map((item)=><li key={item}><Typography variant="caption">Missing: {item}</Typography></li>)}
							</Box>
						)}
						{activationMissingItems.length===0 && !this.state.geoad_enabled && (
							<Typography variant="caption" color="success.main" sx={{display:'block',mt:0.5}}>
								Setup complete. You can enable Boostly and save the configuration.
							</Typography>
						)}
					</Box>
					<Switch
						checked={this.state.geoad_enabled}
						color="warning"
						disabled={this.state.isSaving || (!this.state.geoad_enabled && activationMissingItems.length>0)}
						inputProps={{'aria-label':'Enable Boostly bookings'}}
						onChange={(event)=>this.toggleBoostlyEnabled(event.target.checked)}
					/>
				</Box>
			</span>
		);
	}

    render() {
	  	return(
			<>
				<ETVPage width="standard" icon={<SettingsOutlinedIcon fontSize="large"/>} title="Boostly settings"
					description="Complete the building, screen, pricing, and weekly calendar settings before enabling Boostly bookings."
					loading={this.state.isGeoadLoading || this.state.isSaving || this.state.isOperatorLoading}
					bottomActions={
						<ETVActionButton action="save" disabled={!this.state.selected_location_id}
							loading={this.state.isSaving} onClick={()=>this.saveConfig()}/>
					}>

					<Paper variant="outlined" sx={{p:2,mb:2}}>
					<Typography variant="h6" sx={{mb:1}}>Building and Boostly bookings</Typography>
					<Grid container spacing={2} alignItems="center">
					<Grid item xs={12} md={7}>
					<FormControl fullWidth variant="outlined">
						<InputLabel>Building</InputLabel>
						<Select
							value={this.state.selected_location_id}
							disabled={this.state.geoad_locations.length===0 || this.state.isSaving}
							onChange={(e)=>this.handleGeoadLocationChange(e.target.value)}
							label="Building" >
							{this.state.geoad_locations.map((location) => (
								<MenuItem key={location.location_id} value={location.location_id}>{location.name}</MenuItem>
							))}
						</Select>
					</FormControl>
					</Grid>
					<Grid item xs={12} md={5}>
						<Stack direction="row" spacing={1.5} alignItems="center">
							<Box sx={{width:96,height:96,border:'1px solid',borderColor:'divider',borderRadius:2,overflow:'hidden',bgcolor:'action.hover',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
								{this.state.location_image_preview?<img alt="Building" src={this.state.location_image_preview} style={{width:'100%',height:'100%',objectFit:'cover'}}/>:<Typography variant="caption" color="text.secondary">512 x 512</Typography>}
							</Box>
							<Box>
								<Button variant="outlined" component="label" disabled={this.state.isSaving || !this.state.selected_location_id}>Upload building image<input hidden accept="image/*" type="file" onChange={(event)=>this.handleLocationImageChange(event)}/></Button>
								<Typography variant="caption" color="text.secondary" sx={{display:'block',mt:0.75}}>Center-cropped to a 512 x 512 JPG.</Typography>
							</Box>
						</Stack>
					</Grid>
					</Grid>
					{this.state.geoad_locations.length===0 && (
						<Typography style={{marginTop:10}} variant="body2">No buildings found for this user.</Typography>
					)}
					{this.state.selected_location_id && this.state.boostly_available===true && (
						<Box sx={{mt:2}}>
							<FormControl fullWidth variant="outlined">
								<InputLabel>Boostly operator</InputLabel>
								<Select
									value={this.state.selected_operator_scope_id}
									disabled={this.state.isSaving || this.state.isOperatorLoading}
									onChange={(event)=>this.selectBoostlyOperator(event.target.value)}
									label="Boostly operator">
									<MenuItem value="" disabled><em>Select an operator</em></MenuItem>
									{this.state.boostly_operators.map((operator) => (
										<MenuItem key={operator.id} value={operator.id} disabled={operator.status!=='ACTIVE'}>
											{operator.name} - {operator.operator_name}
											{operator.status!=='ACTIVE'?' (Inactive)':''}
										</MenuItem>
									))}
								</Select>
							</FormControl>
							{this.state.boostly_operators.every((operator)=>operator.status!=='ACTIVE') && (
								<Typography sx={{mt:1}} variant="body2" color="text.secondary">
									No active Boostly operators are available in this country yet.
								</Typography>
							)}
						</Box>
					)}
					{this.state.selected_location_id && this.state.boostly_available===false && (
						<Typography sx={{mt:2}} variant="body2" color="text.secondary">
							Boostly is not currently available in this building&apos;s country.
						</Typography>
					)}
					</Paper>

					{this.renderSettings()}

				</ETVPage>

				<ETVMessagePopup
					open={this.state.window==='message_div'}
					onClose={()=>{this.closeInfoMessage()}}
					type={this.state.isSaving?'progress':this.state.info_type}
					allowClose={!this.state.isSaving}
					title={this.state.info_title}
					message={this.state.info_message}
				/>
			</>);
	}
}

export default GeoAdConfig
